const heroVideoPlayers = new Set();

function loadVimeoApi() {
  if (window.Vimeo && window.Vimeo.Player) {
    return Promise.resolve();
  }
  if (!loadVimeoApi.promise) {
    loadVimeoApi.promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://player.vimeo.com/api/player.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Vimeo player API failed to load'));
      document.head.appendChild(script);
    });
  }
  return loadVimeoApi.promise;
}

class HeroBannerVideoPlayback {
  constructor(container) {
    this.container = container;
    this.button = container.querySelector('[data-hero-video-playback]');
    this.nativeVideo = container.querySelector('video');
    this.iframe = container.querySelector('iframe');

    this.embedPlayer = null;
    this.embedReady = null;
    this.embedPaused = false;
    this.youtubeMessageBound = false;

    if (!this.button) return;

    this.button.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggle().catch(() => {});
    });

    this.container.addEventListener('click', (event) => {
      if (event.target.closest('[data-hero-video-playback]')) return;
      if (!this.isVisible()) return;
      this.toggle().catch(() => {});
    });

    if (this.nativeVideo) {
      this.nativeVideo.addEventListener('play', () => this.setPaused(false));
      this.nativeVideo.addEventListener('pause', () => this.setPaused(true));
      this.onViewportChange();
      return;
    }

    heroVideoPlayers.add(this);
    this.onViewportChange();
  }

  get embedType() {
    if (this.iframe?.classList.contains('js-youtube')) return 'youtube';
    if (this.iframe?.classList.contains('js-vimeo')) return 'vimeo';
    return null;
  }

  isVisible() {
    return window.getComputedStyle(this.container).display !== 'none';
  }

  onViewportChange() {
    if (this.nativeVideo) {
      if (this.isVisible()) {
        this.nativeVideo.play().catch(() => {});
      } else {
        this.nativeVideo.pause();
      }
      this.setPaused(this.nativeVideo.paused);
      return;
    }

    if (!this.isVisible()) {
      this.pauseEmbed();
      return;
    }

    if (this.embedPlayer) {
      this.playEmbed();
      return;
    }

    this.prepareEmbed();
  }

  setPaused(isPaused) {
    this.button.classList.toggle('is-paused', isPaused);
    this.button.setAttribute('aria-pressed', String(!isPaused));

    const playLabel = this.button.getAttribute('data-label-play');
    const pauseLabel = this.button.getAttribute('data-label-pause');
    if (playLabel && pauseLabel) {
      this.button.setAttribute('aria-label', isPaused ? playLabel : pauseLabel);
    }
  }

  prepareEmbed() {
    return this.getEmbedPlayer().catch(() => {});
  }

  ensureIframeLoaded() {
    const src = this.iframe?.dataset.src;
    if (!src) {
      return Promise.resolve();
    }

    if (this.iframe.src) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.iframe.addEventListener('load', () => resolve(), { once: true });
      this.iframe.src = src;
    });
  }

  getEmbedPlayer() {
    if (this.embedPlayer) {
      return Promise.resolve(this.embedPlayer);
    }

    if (!this.isVisible()) {
      return Promise.reject(new Error('Video is hidden'));
    }

    if (!this.embedReady) {
      this.embedReady = this.ensureIframeLoaded()
        .then(() => this.createEmbedPlayer())
        .catch((error) => {
          this.embedReady = null;
          throw error;
        });
    }

    return this.embedReady;
  }

  postYouTubeMessage(message) {
    this.iframe?.contentWindow?.postMessage(JSON.stringify(message), '*');
  }

  handleYouTubeMessage(event) {
    if (event.source !== this.iframe?.contentWindow) return;
    if (typeof event.data !== 'string') return;

    try {
      const data = JSON.parse(event.data);
      if (data.event !== 'infoDelivery' || data.info?.playerState === undefined) return;

      if (data.info.playerState === 1) {
        this.embedPaused = false;
        this.setPaused(false);
      } else if (data.info.playerState === 2) {
        this.embedPaused = true;
        this.setPaused(true);
      }
    } catch (error) {}
  }

  createEmbedPlayer() {
    if (this.embedType === 'youtube') {
      if (!this.youtubeMessageBound) {
        this.handleYouTubeMessage = this.handleYouTubeMessage.bind(this);
        window.addEventListener('message', this.handleYouTubeMessage);
        this.youtubeMessageBound = true;
      }

      this.embedPlayer = { type: 'youtube' };
      this.embedPaused = false;
      this.postYouTubeMessage({ event: 'listening' });
      this.postYouTubeMessage({ event: 'command', func: 'playVideo', args: '' });
      this.setPaused(false);
      return Promise.resolve(this.embedPlayer);
    }

    if (this.embedType === 'vimeo') {
      return loadVimeoApi().then(async () => {
        const player = new window.Vimeo.Player(this.iframe);
        this.embedPlayer = player;
        player.on('play', () => this.setPaused(false));
        player.on('pause', () => this.setPaused(true));
        await player.play();
        this.setPaused(false);
        return player;
      });
    }

    return Promise.reject(new Error('Unsupported video type'));
  }

  async toggle() {
    if (this.nativeVideo) {
      if (this.nativeVideo.paused) {
        await this.nativeVideo.play();
      } else {
        this.nativeVideo.pause();
      }
      return;
    }

    await this.getEmbedPlayer();

    if (this.embedType === 'youtube') {
      if (this.embedPaused) {
        this.postYouTubeMessage({ event: 'command', func: 'playVideo', args: '' });
        this.embedPaused = false;
        this.setPaused(false);
      } else {
        this.postYouTubeMessage({ event: 'command', func: 'pauseVideo', args: '' });
        this.embedPaused = true;
        this.setPaused(true);
      }
      return;
    }

    if (this.embedType === 'vimeo') {
      if (await this.embedPlayer.getPaused()) {
        await this.embedPlayer.play();
      } else {
        await this.embedPlayer.pause();
      }
    }
  }

  playEmbed() {
    if (!this.embedPlayer) return;

    if (this.embedType === 'youtube') {
      this.postYouTubeMessage({ event: 'command', func: 'playVideo', args: '' });
      this.embedPaused = false;
      this.setPaused(false);
      return;
    }

    if (this.embedType === 'vimeo') {
      this.embedPlayer.play().then(() => this.setPaused(false)).catch(() => {});
    }
  }

  pauseEmbed() {
    if (!this.embedPlayer) return;

    if (this.embedType === 'youtube') {
      this.postYouTubeMessage({ event: 'command', func: 'pauseVideo', args: '' });
      this.embedPaused = true;
      this.setPaused(true);
      return;
    }

    if (this.embedType === 'vimeo') {
      this.embedPlayer.pause().then(() => this.setPaused(true)).catch(() => {});
    }
  }
}

function initHeroBannerVideos(root) {
  const scope = root && root.querySelector ? root : document;

  scope.querySelectorAll('.hero-banner__video').forEach((container) => {
    if (container.dataset.heroVideoInit) return;
    container.dataset.heroVideoInit = 'true';
    new HeroBannerVideoPlayback(container);
  });
}

function initHeroBannerViewportListener() {
  if (initHeroBannerViewportListener.initialized) return;
  initHeroBannerViewportListener.initialized = true;

  window.matchMedia('(min-width: 768px)').addEventListener('change', () => {
    heroVideoPlayers.forEach((player) => player.onViewportChange());
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initHeroBannerViewportListener();
  initHeroBannerVideos(document);
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target?.querySelector?.('.hero-banner__video')) {
    initHeroBannerVideos(event.target);
  }
});

if (!window.customElements.get('hero-banner')) {
  const HeroBanner = class extends HTMLElement {
    connectedCallback() {
      this.prevButton = this.querySelector('[data-hero-banner-prev]');
      this.nextButton = this.querySelector('[data-hero-banner-next]');
      this.pagination = this.querySelector('[data-hero-banner-pagination]');

      if (!this.classList.contains('swiper')) return;

      new Swiper(this, {
        slidesPerView: 1,
        navigation: {
          prevEl: this.prevButton,
          nextEl: this.nextButton,
        },
        pagination: {
          el: this.pagination,
          type: 'bullets',
          clickable: true,
          bulletClass: 'hero-banner-carousel-pagination__bullet',
        },
      });
    }
  };

  window.customElements.define('hero-banner', HeroBanner);
}
